import React, { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, Upload, FileWarning, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";

export default function BulkImportPanel({ onImported }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("afc_admin_token");
      const res = await fetch(`${API}/admin/products/csv-template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "afc_products_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download template");
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please choose a .csv file");
      e.target.value = "";
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created > 0) {
        toast.success(`Imported ${data.created} product${data.created > 1 ? "s" : ""}`);
        await onImported?.();
      } else if (data.errors?.length) {
        toast.error(`No products imported — ${data.errors.length} row error${data.errors.length > 1 ? "s" : ""}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Import failed";
      toast.error(detail);
      setResult({ created: 0, errors: [{ row: "-", error: detail }], total_rows: 0 });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-4" data-testid="bulk-import-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="font-display text-base font-semibold text-[#0B132B]">Bulk Import Products</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Upload a CSV of products. Use the template so columns and separators match.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={downloadTemplate}
            className="rounded-full"
            data-testid="download-csv-template"
          >
            <Download className="w-4 h-4 me-2" /> Download Template
          </Button>
          <label className="inline-flex">
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleFile}
              data-testid="csv-file-input"
            />
            <Button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="rounded-full bg-[#1E5FD9] hover:bg-[#174AB0]"
              data-testid="csv-import-btn"
            >
              <Upload className="w-4 h-4 me-2" /> {uploading ? "Importing…" : "Import CSV"}
            </Button>
          </label>
        </div>
      </div>

      {result && (
        <div className="mt-4 border-t border-slate-100 pt-4" data-testid="csv-import-result">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            {result.created > 0 && (
              <span className="inline-flex items-center gap-1.5 text-emerald-700 font-medium">
                <CheckCircle2 className="w-4 h-4" /> {result.created} imported
              </span>
            )}
            {result.errors?.length > 0 && (
              <span className="inline-flex items-center gap-1.5 text-rose-700 font-medium">
                <FileWarning className="w-4 h-4" /> {result.errors.length} row{result.errors.length > 1 ? "s" : ""} skipped
              </span>
            )}
            <button
              onClick={() => setResult(null)}
              className="ms-auto text-slate-400 hover:text-slate-600"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-3 max-h-52 overflow-y-auto border border-rose-100 rounded-xl bg-rose-50/40">
              <table className="w-full text-xs">
                <thead className="bg-rose-50 text-rose-700">
                  <tr>
                    <th className="p-2 text-start w-16">Row</th>
                    <th className="p-2 text-start">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.map((e, i) => (
                    <tr key={i} className="border-t border-rose-100" data-testid={`csv-error-row-${i}`}>
                      <td className="p-2 font-mono text-slate-600">{e.row}</td>
                      <td className="p-2 text-slate-700">{e.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
